import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Alert, KeyboardAvoidingView, Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';
import type { AbaId, AlertaItem, Recur, Task, RecurType } from '../store/types';
import { newTask, saveTask, excluirTarefa, toggleSub, naData, camposMudados, salvarOcorrencia, type OndeAplicar } from '../store/planner';
import { getDoc } from '../store/store';
import { EMOJIS_PAUSA, emojiDaPausa } from '../lib/pausas';
import { MODOS_AVISO } from '../lib/avisos';
import { setDoc, useDoc } from '../store/store';
import { linkOptions, linkLabel, linkHref, linkExists } from '../store/links';
import { isRepeating, recurLabel } from '../lib/recur';
import { autoEmoji } from '../lib/emoji';
import { brYmd, longDate, parseYmd, today, uid, ymd, hm } from '../lib/date';
import { blocos, durCampo, durLabel, endTime, fromMin, parseDur, toMin } from '../lib/time';
import { alertItens, alertasDe, alertasResumo } from '../lib/alertas';
import { AlertasEditor } from './AlertasEditor';
import { ABAS, C, alpha, aba, useCores } from './theme';
import { ActionSheet, Btn, Chip, Field, Row, Sheet, Swatches, type Action } from './ui';
import { PickerModal } from './Pickers';
import { Ordenavel, ScrollArrastavel, semArrastar } from './Arrastar';
import { ItemDeslizar, excluirPequeno, inserirEm } from './ItemDeslizar';
import { EmojiPicker } from './EmojiPicker';
import { CampoRico } from './CampoRico';
import type { RicoCampo } from '../store/types';
import { FusoSheet } from './FusoSheet';
import { AnexosEditor } from './Anexos';
import { abrirRota, juntarLocal, separarLocal } from '../lib/mapa';
import { abrirGrupos } from './Grupos';
import { fusoAparelho, noAparelho, nomeFuso } from '../lib/fuso';
import { t, tn, N_ } from '../lib/i18n';
import { horaTela } from '../lib/date';

/** Dia aberto no My Planner: o botão + cria a tarefa nesse dia. */
export const diaAberto = { atual: today() };

type OpenArgs = { task?: Task; date?: string; prefill?: Partial<Task>; onSaved?: (t: Task) => void };
const Ctx = createContext<{ open: (a?: OpenArgs) => void } | null>(null);
export const useTaskSheet = () => { const c = useContext(Ctx); if (!c) throw new Error('TaskSheetProvider ausente'); return c; };

const ENERGIA = [N_('Relaxar'), N_('Baixa'), N_('Leve'), N_('Média'), N_('Alta'), N_('Máxima')];
export const energiaLabel = (n: number) => (n ? `⚡${n} · ${t(ENERGIA[n])}` : '🌙 ' + t('Relaxar'));
const DIAS_CURTOS = ['@letra-dom', '@letra-seg', '@letra-ter', '@letra-qua', '@letra-qui', '@letra-sex', '@letra-sab'];
const FREQ: [RecurType, string][] = [['none', N_('Não se repete')], ['daily', N_('Todo dia')], ['weekly', N_('Semanal')], ['monthly', N_('Todo mês')], ['yearly', N_('Todo ano')], ['custom', N_('Personalizado')]];

/** As opções da pergunta "Onde aplicar a mudança?" de uma tarefa que se repete (B1). Usada também no "+ Subtarefa" do cartão. */
export function opcoesOnde(date: string, serieMudou: boolean, aplicar: (o: OndeAplicar) => void): Action[] {
  const hoje = date === today();
  return [
    ...(serieMudou ? [] : [
      { label: hoje ? t('Só hoje') : t('Só neste dia ({data})', { data: brYmd(date) }), onPress: () => aplicar('dia') },
      { label: t('Só esta semana'), onPress: () => aplicar('semana') },
    ]),
    { label: t('Esta e as próximas'), onPress: () => aplicar('futuras') },
  ];
}

type PickerState = { mode: 'date' | 'time'; value: Date; onPick: (d: Date) => void } | null;

export function TaskSheetProvider({ children }: { children: ReactNode }) {
  const [args, setArgs] = useState<OpenArgs | null>(null);
  const [key, setKey] = useState(0);
  const open = useCallback((a: OpenArgs = {}) => { setArgs(a); setKey(k => k + 1); }, []);
  return (
    <Ctx.Provider value={{ open }}>
      {children}
      {args && <Ficha key={key} args={args} onClose={() => setArgs(null)} />}
    </Ctx.Provider>
  );
}

function Ficha({ args, onClose }: { args: OpenArgs; onClose: () => void }) {
  const router = useRouter();
  const { corDe, liberada, accent } = useCores();
  useDoc('casa'); useDoc('anotacoes'); useDoc('garantias'); // atualiza as opções de vínculo
  const editando = !!args.task;
  const date = args.date ?? args.task?.start ?? today();
  const aj = useDoc('ajustes');
  // 0.67 (B1): a tarefa como ela é neste dia (a série + a mudança "só hoje"/"só esta semana", se houver)
  const [aberta] = useState<Task | null>(() => {
    if (!args.task) return null;
    const serie = getDoc('planner').tasks.find(x => x.id === args.task!.id);
    return serie ? naData(serie, date) : args.task;
  });
  const [onde, setOnde] = useState<Action[] | null>(null);
  const [tk, setT] = useState<Task>(() => {
    const base = aberta ?? newTask({ start: args.date ?? today(), ...args.prefill });
    // ficha nova já abre com três linhas de subtarefa para preencher
    const faltam = editando ? 0 : Math.max(0, 3 - base.subtasks.length);
    return { ...base, notes: separarLocal(base.notes).resto, subtasks: [...base.subtasks, ...Array.from({ length: faltam }, () => ({ id: uid(), text: '' }))] };
  });
  const [aberto, setAberto] = useState<string | null>(null);
  // local (endereço) fica na 1ª linha das anotações ("📍 ..."), como na agenda do Google
  const [local, setLocal] = useState(() => separarLocal(aberta?.notes ?? args.task?.notes ?? args.prefill?.notes ?? '').local);
  const [picker, setPicker] = useState<PickerState>(null);
  const [emojis, setEmojis] = useState(false);
  const [maisFim, setMaisFim] = useState(false);
  const [maisHora, setMaisHora] = useState(false);
  const [fusoAberto, setFusoAberto] = useState(false);
  const [durTxt, setDurTxt] = useState(durCampo(tk.duration));
  const [linkAba, setLinkAba] = useState<AbaId | null>(null);
  const [del, setDel] = useState<Action[] | null>(null);
  const subRefs = useRef<Record<string, TextInput | null>>({});

  const cor = tk.color ?? corDe(tk.area);
  const set = (p: Partial<Task>) => setT(x => ({ ...x, ...p }));
  const toggle = (k: string) => { setAberto(a => (a === k ? null : k)); setMaisFim(false); setMaisHora(false); };
  const asDate = (s: string) => parseYmd(s);
  const asTime = (h: string) => { const d = new Date(); d.setHours(Math.floor(toMin(h) / 60), toMin(h) % 60, 0, 0); return d; };
  const subDoneHoje = tk.subDone[date] ?? [];

  function setTitle(title: string) {
    set({ title, ...(tk.emojiManual ? {} : { emoji: autoEmoji(title) }) });
  }
  function setDur(n: number) {
    const d = Math.max(1, Math.min(24 * 60, Math.round(n)));
    set({ duration: d }); setDurTxt(durCampo(d));
  }
  /** Confirma a duração digitada ("90", "1:30" ou "01:30"). */
  function confirmarDur() {
    const n = parseDur(durTxt);
    if (n) setDur(n); else setDurTxt(durCampo(tk.duration));
  }
  /** Muda o início; as pausas acompanham, mantendo a mesma distância do início. */
  function setHora(novo: string) {
    setT(x => {
      if (!x.time || !x.pauses.length) return { ...x, time: novo };
      const dif = toMin(novo) - toMin(x.time);
      return { ...x, time: novo, pauses: x.pauses.map(p => ({ ...p, at: fromMin(toMin(p.at) + dif) })) };
    });
  }
  // alertas como lista (aceita o formato antigo)
  const alertas = alertItens(tk.alerts);
  const setAlertas = (f: (a: AlertaItem[]) => AlertaItem[]) => setT(x => ({ ...x, alerts: alertasDe(f(alertItens(x.alerts))) }));
  /** Marcar/desmarcar item das anotações salva na hora (só a anotação) quando a tarefa já existe. */
  function salvarNotas(resto: string, rico?: RicoCampo) {
    if (!editando) return;
    const notes = juntarLocal(local, resto);
    setDoc('planner', d => ({ tasks: d.tasks.map(x => (x.id !== tk.id ? x
      // anotação mudada só neste dia (0.67): marca só nela
      : x.excecoes?.[date]?.notes !== undefined ? { ...x, excecoes: { ...x.excecoes, [date]: { ...x.excecoes[date], notes } }, updatedAt: new Date().toISOString() }
      : { ...x, notes, ...(rico ? { notasRico: rico } : {}), updatedAt: new Date().toISOString() })) }));
  }

  /** Mesmo acabamento do Salvar (para comparar o que mudou sem achar diferença onde não há). */
  const acabar = (x: Task, loc: string): Task => ({
    ...x, title: x.title.trim(),
    subtasks: x.subtasks.map(s => ({ ...s, text: s.text.trim() })).filter(s => s.text),
    pauses: x.time ? x.pauses : [],
    tz: x.time ? x.tz || fusoAparelho() : null,
    notes: juntarLocal(loc, x.notes.replace(/\s+$/, '')),
    alerts: alertasDe(alertItens(x.alerts).filter(a => (x.time || a.tipo === 'hora') && (a.tipo !== 'hora' || !!a.hora))),
    recur: x.recur.type === 'weekly' && !x.recur.days?.length ? { ...x.recur, days: [parseYmd(x.start).getDay()] } : x.recur,
  });

  function salvar() {
    if (!tk.title.trim()) return Alert.alert(t('Dê um nome à tarefa.'));
    if (tk.end && tk.end < tk.start) return Alert.alert(t('A data de fim é anterior à de início.'));
    const final: Task = acabar(tk, local);
    // 0.67 (B1): tarefa que se repete: perguntar onde aplicar a mudança (só hoje, só esta semana, esta e as próximas)
    const serie = editando && aberta ? getDoc('planner').tasks.find(x => x.id === tk.id) : undefined;
    if (serie && aberta && isRepeating(serie)) {
      const antes = acabar({ ...aberta, notes: separarLocal(aberta.notes).resto }, separarLocal(aberta.notes).local);
      const serieMudou = final.start !== antes.start || final.end !== antes.end || JSON.stringify(final.recur) !== JSON.stringify(antes.recur);
      if (!serieMudou && !camposMudados(antes, final).length) {
        // nada da tarefa mudou (só marcações do dia, que já foram salvas): guarda as marcações
        setDoc('planner', d => ({ tasks: d.tasks.map(x => (x.id === tk.id ? { ...x, done: final.done, subDone: final.subDone } : x)) }));
        onClose();
        return;
      }
      const aplicar = (o: OndeAplicar) => { const r = salvarOcorrencia(final, antes, date, o); args.onSaved?.(r); onClose(); };
      setOnde(opcoesOnde(date, serieMudou, aplicar));
      return;
    }
    saveTask(final);
    args.onSaved?.(final);
    onClose();
  }

  function duplicar() {
    const agora = new Date().toISOString();
    const copia = newTask({ ...tk, notes: juntarLocal(local, tk.notes), gcal: undefined, excecoes: undefined, id: uid(), title: t('{titulo} (cópia)', { titulo: tk.title.trim() }), done: [], subDone: {}, skip: [], createdAt: agora, updatedAt: agora,
      subtasks: tk.subtasks.map(x => ({ ...x, id: uid() })) });
    saveTask(copia);
    onClose();
    setTimeout(() => Alert.alert(t('Tarefa duplicada'), t('"{titulo}" foi criada com os mesmos dados.', { titulo: copia.title })), 300);
  }

  function excluir() {
    // texto 05 C: tudo vai para a Lixeira, com o Desfazer
    const fazer = (m: 'one' | 'future' | 'all') => { excluirTarefa(tk.id, m, date); onClose(); };
    if (!isRepeating(tk)) {
      Alert.alert(t('Excluir tarefa?'), tk.title, [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => fazer('all') }]);
      return;
    }
    setDel([
      { label: t('Somente esta tarefa'), onPress: () => fazer('one') },
      { label: t('Esta e todas as futuras'), onPress: () => fazer('future') },
      { label: t('Todas (passadas, esta e futuras)'), onPress: () => fazer('all'), destructive: true },
    ]);
  }

  // ─── textos de resumo ───
  const horaTxt = tk.time ? `${horaTela(tk.time)} – ${horaTela(endTime(tk.time, tk.duration))}` : t('Sem horário');
  const alertTxt = alertasResumo(tk.time ? alertas : alertas.filter(a => a.tipo === 'hora'));
  const linkTxt = tk.link ? linkLabel(tk.link) : t('Nenhum');
  const resumoPausas = tk.time && tk.pauses.length
    ? t('Atividade: {blocos}', { blocos: blocos(tk.time, tk.duration, tk.pauses).map(b => t('{de} às {ate}', { de: horaTela(b.de), ate: horaTela(b.ate) })).join(' / ') }) : '';

  return (
    <Sheet visible onClose={onClose} title={editando ? t('Editar tarefa') : t('Nova tarefa')}
      right={<Pressable onPress={salvar} hitSlop={10}><Text style={{ color: cor, fontWeight: '800', fontSize: 16 }}>{t('Salvar')}</Text></Pressable>}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
        <ScrollArrastavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
          {/* Nome e ícone */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <Pressable onPress={() => setEmojis(true)} style={{ width: 58, height: 58, borderRadius: 18, backgroundColor: alpha(cor, 0.18),
              borderWidth: 2, borderColor: cor, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: 30 }}>{tk.emoji}</Text>
            </Pressable>
            <TextInput value={tk.title} onChangeText={setTitle} placeholder={t('Nome da tarefa')} placeholderTextColor={C.mute}
              autoFocus={!editando && !tk.title} style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 22, fontWeight: '700' }} />
          </View>

          <Row icon="🏷️" label={t('Aba')} value={aba(tk.area).nome} open={aberto === 'aba'} onPress={() => toggle('aba')}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {ABAS.map(a => <Chip key={a.id} cor={corDe(a.id)} on={tk.area === a.id} label={`${a.icon} ${a.nome}`} small
                onPress={() => { set({ area: a.id }); setAberto(null); }} />)}
            </View>
            <Btn outline cor={cor} label={t('Mais emojis')} onPress={() => setEmojis(true)} />
          </Row>

          <Row icon="🎨" label={t('Cor')} value={tk.color ? '' : t('Padrão da aba')} open={aberto === 'cor'} onPress={() => toggle('cor')}
            right={<View style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: tk.color ?? corDe(tk.area) }} />}>
            <Swatches value={tk.color} onChange={c => { set({ color: c }); setAberto(null); }}
              extra={<Chip cor={cor} on={!tk.color} small label={t('Padrão da aba')} onPress={() => { set({ color: null }); setAberto(null); }} />} />
          </Row>

          {/* Datas */}
          <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 14, gap: 12 }}>
            <Pressable style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
              onPress={() => setPicker({ mode: 'date', value: asDate(tk.start), onPick: d => set({ start: ymd(d) }) })}>
              <Text style={{ fontSize: 18, width: 26, textAlign: 'center' }}>📅</Text>
              <Text style={{ color: C.sub, width: 86 }}>{t('Início')}</Text>
              <Text style={{ color: C.text, flex: 1, textAlign: 'right' }}>{longDate(tk.start)}</Text>
            </Pressable>
            <View style={{ height: 1, backgroundColor: C.line }} />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Text style={{ fontSize: 18, width: 26, textAlign: 'center' }}>🏁</Text>
              <Text style={{ color: C.sub, width: 86 }}>{t('Fim')}</Text>
              {tk.end ? (<>
                <Pressable style={{ flex: 1 }} onPress={() => setPicker({ mode: 'date', value: asDate(tk.end!), onPick: d => set({ end: ymd(d) }) })}>
                  <Text style={{ color: C.text, textAlign: 'right' }}>{longDate(tk.end)}</Text>
                </Pressable>
                <Pressable onPress={() => setMaisFim(v => !v)} hitSlop={10} accessibilityLabel={t('Mais opções')}><Text style={{ color: C.sub, fontSize: 20, fontWeight: '800' }}>⋮</Text></Pressable>
              </>) : (
                <Pressable style={{ flex: 1, alignItems: 'flex-end' }} onPress={() => setPicker({ mode: 'date', value: asDate(tk.start), onPick: d => set({ end: ymd(d) }) })}>
                  <Text style={{ color: C.text }}>{t('Sem data de fim')}</Text>
                </Pressable>
              )}
            </View>
            {tk.end && maisFim && (
              <View style={{ alignItems: 'flex-end' }}>
                <Chip cor={cor} on={false} small label={t('Sem data de fim')} onPress={() => { set({ end: null }); setMaisFim(false); }} />
              </View>
            )}
          </View>

          {/* Horário */}
          <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 14, gap: 10 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Text style={{ fontSize: 18, width: 26, textAlign: 'center' }}>🕐</Text>
              <Text style={{ color: C.sub, width: 86 }}>{t('Horário')}</Text>
              {tk.time ? (
                <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <Chip cor={cor} on small label={horaTela(tk.time)} onPress={() => setPicker({ mode: 'time', value: asTime(tk.time!), onPick: d => setHora(hm(d)) })} />
                  <Text style={{ color: C.sub }}>{t('até')}</Text>
                  <Chip cor={cor} on={false} small label={horaTela(endTime(tk.time, tk.duration))} onPress={() => setPicker({ mode: 'time', value: asTime(endTime(tk.time!, tk.duration)),
                    onPick: d => { const dif = toMin(hm(d)) - toMin(tk.time!); setDur(dif > 0 ? dif : dif + 1440); } })} />
                  <Pressable onPress={() => setMaisHora(v => !v)} hitSlop={10} accessibilityLabel={t('Mais opções')}><Text style={{ color: C.sub, fontSize: 20, fontWeight: '800' }}>⋮</Text></Pressable>
                </View>
              ) : (
                <Pressable style={{ flex: 1, alignItems: 'flex-end' }}
                  onPress={() => setPicker({ mode: 'time', value: asTime('09:00'), onPick: d => setHora(hm(d)) })}>
                  <Text style={{ color: C.text }}>{t('Sem horário')}</Text>
                </Pressable>
              )}
            </View>
            {tk.time && maisHora && (
              <View style={{ alignItems: 'flex-end' }}>
                <Chip cor={cor} on={false} small label={t('Sem horário')} onPress={() => { set({ time: null }); setMaisHora(false); }} />
              </View>
            )}
            {tk.time && (
              <Pressable onPress={() => setFusoAberto(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <Text style={{ fontSize: 16, width: 26, textAlign: 'center' }}>🌐</Text>
                <Text style={{ color: C.sub, width: 86 }}>{t('Fuso')}</Text>
                <Text style={{ color: C.text, flex: 1, textAlign: 'right' }} numberOfLines={1}>{nomeFuso(tk.tz || fusoAparelho())}</Text>
                <Text style={{ color: C.mute }}>▾</Text>
              </Pressable>
            )}
            {tk.time && tk.tz && tk.tz !== fusoAparelho() && (() => { const l = noAparelho(tk, tk.start); return (
              <Text style={{ color: C.sub, fontSize: 12, textAlign: 'right' }}>{t('No seu fuso: {hora}{dia}', { hora: horaTela(l.hora), dia: l.dia !== tk.start ? ` (${brYmd(l.dia)})` : '' })}</Text>); })()}
          </View>

          <Row icon="⏱" label={t('Duração')} value={durLabel(tk.duration)} open={aberto === 'dur'} onPress={() => toggle('dur')}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Chip cor={cor} on={false} label="−5" onPress={() => setDur(tk.duration - 5)} />
              <Field value={durTxt} selectTextOnFocus placeholder="30 / 01:30" style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 18 }}
                keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : Platform.OS === 'android' ? 'phone-pad' : 'default'}
                onChangeText={v => setDurTxt(v.replace(/[^\d:hH.,]/g, '').slice(0, 5))} onSubmitEditing={confirmarDur} onBlur={confirmarDur} />
              <Chip cor={cor} on label={t('OK')} onPress={confirmarDur} />
              <Chip cor={cor} on={false} label="+5" onPress={() => setDur(tk.duration + 5)} />
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {[5, 10, 15, 30, 45, 60, 90, 120].map(m => <Chip key={m} cor={cor} small on={tk.duration === m} label={durLabel(m)} onPress={() => setDur(m)} />)}
            </View>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Digite em minutos (90) ou em horas e minutos (1:30 ou 01:30) e toque em OK.')}</Text>
            <Text style={{ color: C.text, fontSize: 13, backgroundColor: C.card2, padding: 10, borderRadius: 10 }}>
              {tk.time ? t('Início {ini} · Fim {fim} · Duração {dur}', { ini: horaTela(tk.time), fim: horaTela(endTime(tk.time, tk.duration)), dur: durLabel(tk.duration) })
                : t('Duração {dur}', { dur: durLabel(tk.duration) })}
            </Text>
          </Row>

          <Row icon="🔄" label={t('Frequência')} value={recurLabel(tk.recur, tk.start)} open={aberto === 'freq'} onPress={() => toggle('freq')}>
            <FrequenciaEditor recur={tk.recur} start={tk.start} cor={cor} onChange={recur => set({ recur })} />
            <Btn cor={cor} label={t('Pronto')} onPress={() => {
              if (tk.recur.type === 'custom' && !tk.recur.every) set({ recur: { ...tk.recur, every: 2 } });
              setAberto(null);
            }} />
          </Row>

          <Row icon="🔔" label={t('Alertas')} value={alertTxt} open={aberto === 'alert'} onPress={() => toggle('alert')}>
            {/* texto 10 (A3): o mesmo editor de alertas que as outras abas vão usar (components/AlertasEditor) */}
            <AlertasEditor itens={alertas} onChange={l => setAlertas(() => l)} temHorario={!!tk.time} horaPadrao={tk.time} cor={cor} aba={tk.area} />
            <Btn cor={cor} label={t('OK')} onPress={() => setAberto(null)} />
          </Row>

          <Row icon="⚡" label={t('Energia')} value={energiaLabel(tk.energy)} open={aberto === 'en'} onPress={() => toggle('en')}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {[0, 1, 2, 3, 4, 5].map(n => <Chip key={n} cor={cor} on={tk.energy === n} label={n ? `⚡${n} ${t(ENERGIA[n])}` : '🌙 ' + t('Relaxar')}
                onPress={() => { set({ energy: n }); setAberto(null); }} />)}
            </View>
          </Row>

          <Row icon="🔗" label={t('Vínculo')} value={linkTxt} open={aberto === 'link'} onPress={() => { toggle('link'); setLinkAba(null); }}>
            {tk.link && (
              <View style={{ gap: 8 }}>
                {!linkExists(tk.link) && <Text style={{ color: C.warn, fontSize: 12 }}>⚠️ {t('O item vinculado foi apagado ou movido.')}</Text>}
                {liberada(tk.link.aba) ? (
                  <Btn cor={cor} label={t('Abrir página')} onPress={() => { const h = linkHref(tk.link!); onClose(); router.push(h as any); }} />
                ) : <Text style={{ color: C.sub, fontSize: 12 }}>{t('A aba {aba} não está assinada. O vínculo fica guardado e volta ao assinar.', { aba: aba(tk.link.aba).nome })}</Text>}
                <Text style={{ color: C.sub, fontSize: 12 }}>{t('Na linha do tempo, tocar no temporizador também abre esta página.')}</Text>
              </View>
            )}
            {!linkAba ? (
              <View style={{ gap: 6 }}>
                <Text style={{ color: C.sub, fontSize: 12 }}>{tk.link ? t('Trocar por:') : t('Escolha a aba:')}</Text>
                <ScrollView nestedScrollEnabled style={{ maxHeight: 290, borderRadius: 10 }} contentContainerStyle={{ gap: 6 }}>
                {ABAS.filter(a => a.id !== 'planner').sort((x, y) => Number(liberada(y.id)) - Number(liberada(x.id))).map(a => (
                  <Pressable key={a.id} onPress={() => {
                    if (!liberada(a.id)) return Alert.alert(t('{aba} está bloqueada', { aba: a.nome }), t('Assine a aba em Ajustes › Compras para vincular.'));
                    if (!linkOptions(a.id).length) { set({ link: { aba: a.id, label: a.nome } }); setAberto(null); return; }
                    setLinkAba(a.id);
                  }} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderRadius: 10, backgroundColor: C.card2, opacity: liberada(a.id) ? 1 : 0.55 }}>
                    <Text style={{ fontSize: 18 }}>{a.icon}</Text>
                    <Text style={{ color: liberada(a.id) ? C.text : C.mute, flex: 1 }}>{a.nome}</Text>
                    <Text style={{ color: C.sub }}>{liberada(a.id) ? '›' : '🔒'}</Text>
                  </Pressable>
                ))}
                </ScrollView>
                {tk.link && <Chip cor={C.danger} on={false} small label={t('Remover vínculo')} onPress={() => { set({ link: null }); setAberto(null); }} />}
              </View>
            ) : (
              <View style={{ gap: 6 }}>
                <Pressable onPress={() => setLinkAba(null)}><Text style={{ color: C.sub }}>‹ {aba(linkAba).nome}</Text></Pressable>
                <ScrollView nestedScrollEnabled style={{ maxHeight: 290, borderRadius: 10 }} contentContainerStyle={{ gap: 6 }}>
                <Pressable onPress={() => { set({ link: { aba: linkAba, label: aba(linkAba).nome } }); setAberto(null); setLinkAba(null); }}
                  style={{ padding: 10, borderRadius: 10, backgroundColor: C.card2 }}><Text style={{ color: C.text }}>{t('A aba inteira')}</Text></Pressable>
                {linkOptions(linkAba).map(o => (
                  <Pressable key={o.id} onPress={() => { set({ link: { aba: linkAba, id: o.id, label: o.label } }); setAberto(null); setLinkAba(null); }}
                    style={{ flexDirection: 'row', gap: 10, padding: 10, borderRadius: 10, backgroundColor: C.card2 }}>
                    <Text>{o.icon.length <= 3 ? o.icon : '🏠'}</Text><Text style={{ color: C.text, flex: 1 }}>{o.label}</Text>
                  </Pressable>
                ))}
                </ScrollView>
              </View>
            )}
          </Row>

          <Row icon="☕" label={t('Pausas')} value={tk.pauses.length ? t('{n} pausa(s)', { n: tk.pauses.length }) : t('Sem pausas')} open={aberto === 'pause'} onPress={() => toggle('pause')}>
            {!tk.time ? <Text style={{ color: C.sub }}>{t('Defina um horário para usar pausas.')}</Text> : (<>
              {tk.pauses.map((p, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Chip cor={cor} on small label={p.at} onPress={() => setPicker({ mode: 'time', value: asTime(p.at),
                    onPick: d => setT(x => ({ ...x, pauses: x.pauses.map((q, j) => (j === i ? { ...q, at: hm(d) } : q)) })) })} />
                  <Field defaultValue={String(p.min)} keyboardType="number-pad" style={{ width: 60, textAlign: 'center', paddingVertical: 8 }}
                    onChangeText={v => { const n = Math.max(1, Math.min(240, Number(v.replace(/\D/g, '')) || 1)); setT(x => ({ ...x, pauses: x.pauses.map((q, j) => (j === i ? { ...q, min: n } : q)) })); }} />
                  <Text style={{ color: C.sub, flex: 1 }}>{t('min · {de} às {ate}', { de: horaTela(p.at), ate: horaTela(fromMin(toMin(p.at) + p.min)) })}</Text>
                  <Pressable onPress={() => set({ pauses: tk.pauses.filter((_, j) => j !== i) })} hitSlop={8}><Text style={{ color: C.sub }}>✕</Text></Pressable>
                </View>
              ))}
              <Chip cor={cor} on={false} small label={t('+ Adicionar pausa')} onPress={() => {
                const ult = tk.pauses.length ? toMin(tk.pauses[tk.pauses.length - 1].at) + 60 : toMin(tk.time!) + Math.min(60, Math.floor(tk.duration / 2));
                set({ pauses: [...tk.pauses, { at: fromMin(ult), min: 10 }] });
              }} />
              {!!resumoPausas && <Text style={{ color: C.text, fontSize: 13, backgroundColor: C.card2, padding: 10, borderRadius: 10 }}>{resumoPausas}</Text>}
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Na hora da pausa aparece um aviso grande na tela, com contagem regressiva.')}</Text>
              {tk.pauses.length > 0 && (<>
                {/* 0.67 (A3/A4): emoji e aviso das pausas desta tarefa; sem escolha, vale Ajustes > Abas > My Planner > Pausas */}
                <Text style={{ color: C.sub, fontSize: 12, marginTop: 4 }}>{t('Emoji da pausa')}</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  <Chip cor={cor} small on={!tk.pausaEmoji} label={t('Usar o padrão ({emoji})', { emoji: emojiDaPausa({}, aj) })} onPress={() => set({ pausaEmoji: undefined })} />
                  {EMOJIS_PAUSA.map(e => <Chip key={e} cor={cor} small on={tk.pausaEmoji === e} label={e} onPress={() => set({ pausaEmoji: e })} />)}
                </View>
                <Text style={{ color: C.sub, fontSize: 12, marginTop: 4 }}>{t('Aviso no começo e no fim de cada pausa')}</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  <Chip cor={cor} small on={!tk.pausaAviso} label={t('Usar o padrão')} onPress={() => set({ pausaAviso: undefined })} />
                  {MODOS_AVISO.map(([m, l]) => <Chip key={m} cor={cor} small on={tk.pausaAviso === m} label={t(l)} onPress={() => set({ pausaAviso: m })} />)}
                </View>
              </>)}
            </>)}
          </Row>

          {/* Subtarefas */}
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('SUBTAREFA')}</Text>
          <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 8 }}>
            <Ordenavel itens={tk.subtasks} chave={s => s.id} alca gap={0} onOrdem={novos => setT(x => ({ ...x, subtasks: novos }))} render={(s, i, arr) => {
              const feito = subDoneHoje.includes(s.id);
              // texto 26 (REGRAS 2): sem ⠿; segurar na parte livre e arrastar move; deslizar → exclui, ← duplica
              const tirarSub = () => excluirPequeno(!!s.text.trim(), () => {
                const pos = tk.subtasks.findIndex(y => y.id === s.id);
                setT(x => ({ ...x, subtasks: x.subtasks.filter(y => y.id !== s.id) }));
                return () => setT(x => (x.subtasks.some(y => y.id === s.id) ? x : { ...x, subtasks: inserirEm(x.subtasks, pos, s) }));
              });
              return (
                <ItemDeslizar onExcluir={tirarSub} onDuplicar={() => setT(x => ({ ...x, subtasks: inserirEm(x.subtasks, x.subtasks.findIndex(y => y.id === s.id) + 1, { ...s, id: uid() }) }))}>
                <View {...arr.alca} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 6, backgroundColor: C.card, borderRadius: 10 }}>
                  <Pressable hitSlop={8} onPress={() => {
                    if (!editando) return;
                    toggleSub(tk.id, s.id, date);
                    setT(x => { const cur = x.subDone[date] ?? []; return { ...x, subDone: { ...x.subDone, [date]: cur.includes(s.id) ? cur.filter(y => y !== s.id) : [...cur, s.id] } }; });
                  }} style={{ width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: feito ? cor : C.mute, backgroundColor: feito ? cor : 'transparent',
                    alignItems: 'center', justifyContent: 'center' }}>
                    {feito && <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>✓</Text>}
                  </Pressable>
                  <TextInput {...semArrastar} ref={r => { subRefs.current[s.id] = r; }} value={s.text} placeholder={t('Subtarefa {n}', { n: i + 1 })} placeholderTextColor={C.mute}
                    style={{ flex: 1, minWidth: 0, color: C.text, paddingVertical: 10, fontSize: 15, textDecorationLine: feito ? 'line-through' : 'none' }}
                    onChangeText={v => setT(x => ({ ...x, subtasks: x.subtasks.map(y => (y.id === s.id ? { ...y, text: v } : y)) }))}
                    returnKeyType="next" blurOnSubmit={false}
                    onSubmitEditing={() => {
                      const n = { id: uid(), text: '' };
                      setT(x => { const arr = [...x.subtasks]; arr.splice(i + 1, 0, n); return { ...x, subtasks: arr }; });
                      setTimeout(() => subRefs.current[n.id]?.focus(), 60);
                    }}
                    onKeyPress={e => {
                      if (e.nativeEvent.key === 'Backspace' && !s.text && tk.subtasks.length > 1) {
                        const prev = tk.subtasks[i - 1]?.id;
                        setT(x => ({ ...x, subtasks: x.subtasks.filter(y => y.id !== s.id) }));
                        if (prev) setTimeout(() => subRefs.current[prev]?.focus(), 60);
                      }
                    }} />
                  <View {...semArrastar}><Pressable hitSlop={8} onPress={tirarSub} accessibilityLabel={t('Excluir')}>
                    <Text style={{ color: C.mute }}>✕</Text></Pressable></View>
                </View>
                </ItemDeslizar>
              );
            }} />
            <Pressable onPress={() => { const n = { id: uid(), text: '' }; set({ subtasks: [...tk.subtasks, n] }); setTimeout(() => subRefs.current[n.id]?.focus(), 60); }}
              style={{ padding: 10 }}><Text style={{ color: cor, fontWeight: '600' }}>{t('+ Adicionar subtarefa')}</Text></Pressable>
          </View>

          {/* Local: endereço com rota no Google Maps, Waze ou Mapas */}
          <Row icon="📍" label={t('Local')} value={local || t('Nenhum')} open={aberto === 'local'} onPress={() => toggle('local')}>
            <Field value={local} onChangeText={setLocal} placeholder={t('Endereço ou nome do lugar')} multiline style={{ minHeight: 48 }} />
            {!!local.trim() && <Btn outline cor={cor} label={'🗺️ ' + t('Ver a rota')} onPress={() => abrirRota(local)} />}
            {!!local.trim() && !!tk.time && (<>
              <Text style={{ color: C.sub, fontSize: 12 }}>🚗 {t('Avisar a hora de sair')}</Text>
              <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                {[15, 30, 45, 60, 90].map(m => {
                  const tem = alertas.some(a => a.tipo === 'antes' && a.min === m);
                  return <Chip key={m} small cor={cor} on={tem} label={t('{n} min antes', { n: m })}
                    onPress={() => setAlertas(a => (tem ? a.filter(x => !(x.tipo === 'antes' && x.min === m)) : [...a, { tipo: 'antes', min: m }]))} />;
                })}
              </View>
            </>)}
          </Row>

          {/* Anexos: arquivo, foto, câmera ou link */}
          <Row icon="📎" label={t('Anexos')} value={tk.anexos?.length ? t('{n} anexo(s)', { n: tk.anexos.length }) : t('Nenhum')} open={aberto === 'anexos'} onPress={() => toggle('anexos')}>
            <AnexosEditor lista={tk.anexos ?? []} cor={cor} onChange={anexos => set({ anexos })} />
          </Row>

          {/* Anotações */}
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('ANOTAÇÕES')}</Text>
          {/* texto 21 (A4): o mesmo editor das caixas da aba Anotações; o texto antigo (com checklist e lista) entra nele */}
          <CampoRico valor={tk.notes} rico={tk.notasRico} cor={cor} aba="planner" converter dica={t('Instruções, receita, observações...')}
            onChange={(tx, rico) => {
              // marcar/desmarcar um item salva na hora (como antes), quando a tarefa já existe
              const marcou = tx !== tk.notes && tx.replace(/☑/g, '☐') === tk.notes.replace(/☑/g, '☐');
              set({ notes: tx, notasRico: rico });
              if (marcou) salvarNotas(tx, rico);
            }} />

          <Btn cor={cor} label={t('Salvar tarefa')} onPress={salvar} style={{ marginTop: 10 }} />
          {editando && <Btn outline cor={cor} label={'📤 ' + t('Enviar para um grupo')} onPress={() => {
            onClose();
            setTimeout(() => abrirGrupos({ envio: { titulo: tk.title.trim(), tipo: 'compromisso', dados: { data: date, hora: tk.time ?? undefined, fuso: tk.time ? tk.tz || fusoAparelho() : undefined,
              duracao: tk.duration, local: local.trim() || undefined, texto: tk.notes.trim() || undefined, emoji: tk.emoji } } }), 400);
          }} />}
          {editando && <Btn outline cor={cor} label={t('Duplicar tarefa')} onPress={duplicar} />}
          {editando && <Btn outline cor={C.danger} label={t('Excluir tarefa')} onPress={excluir} />}
        </ScrollArrastavel>
      </KeyboardAvoidingView>

      <FusoSheet visible={fusoAberto} valor={tk.tz || fusoAparelho()} cor={cor} onClose={() => setFusoAberto(false)} onPick={tz => set({ tz })} />
      {picker && <PickerModal mode={picker.mode} value={picker.value} cor={cor} onPick={picker.onPick} onClose={() => setPicker(null)} />}
      <EmojiPicker visible={emojis} cor={accent} onClose={() => setEmojis(false)} onPick={e => set({ emoji: e, emojiManual: true })} />
      <ActionSheet title={t('Esta tarefa se repete. O que excluir?')} actions={del} onClose={() => setDel(null)} />
      <ActionSheet title={t('Esta tarefa se repete. Onde aplicar a mudança?')} actions={onde} onClose={() => setOnde(null)} />
    </Sheet>
  );
}

/** Escolha da frequência (Não se repete, Todo dia, Semanal...). Usada na tarefa e na tarefa da casa. */
export function FrequenciaEditor({ recur, start, cor, onChange }: { recur: Recur; start: string; cor: string; onChange: (r: Recur) => void }) {
  const setDays = (d: number[]) => onChange({ ...recur, days: d });
  return (<>
            {FREQ.map(([k, l]) => (
              <View key={k} style={{ gap: 8 }}>
                <Pressable onPress={() => onChange({ ...recur, type: k, ...(k === 'custom' && !recur.every ? { every: 2 } : {}) })} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
                  <View style={{ width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: recur.type === k ? cor : C.mute,
                    alignItems: 'center', justifyContent: 'center' }}>
                    {recur.type === k && <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: cor }} />}
                  </View>
                  <Text style={{ color: C.text }}>{t(l)}</Text>
                </Pressable>
                {k === 'weekly' && recur.type === 'weekly' && (() => {
                  const days = recur.days?.length ? recur.days : [parseYmd(start).getDay()];
                  return (
                    <View style={{ gap: 8, paddingLeft: 30 }}>
                      <View style={{ flexDirection: 'row', gap: 6 }}>
                        {[1, 2, 3, 4, 5, 6, 0].map(d => (
                          <Pressable key={d} onPress={() => setDays(days.includes(d) ? days.filter(x => x !== d) : [...days, d])}
                            style={{ width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: days.includes(d) ? cor : C.card2 }}>
                            <Text style={{ color: days.includes(d) ? '#fff' : C.text, fontWeight: '700' }}>{t(DIAS_CURTOS[d])}</Text>
                          </Pressable>
                        ))}
                      </View>
                      <View style={{ flexDirection: 'row', gap: 6 }}>
                        <Chip cor={cor} small on={false} label={t('Dias úteis')} onPress={() => setDays([1, 2, 3, 4, 5])} />
                        <Chip cor={cor} small on={false} label={t('Fins de semana')} onPress={() => setDays([0, 6])} />
                      </View>
                    </View>
                  );
                })()}
                {k === 'monthly' && recur.type === 'monthly' && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingLeft: 30 }}>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(n => {
                      const on = (recur.monthDay ?? parseYmd(start).getDate()) === n;
                      return (
                        <Pressable key={n} onPress={() => onChange({ ...recur, monthDay: n })}
                          style={{ width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? cor : C.card2 }}>
                          <Text style={{ color: on ? '#fff' : C.text, fontSize: 13 }}>{n}</Text>
                        </Pressable>
                      );
                    })}
                  </View>
                )}
                {k === 'custom' && recur.type === 'custom' && (() => {
                  const un = recur.unit ?? 'dia';
                  const unidades: [NonNullable<Recur['unit']>, string][] = [['dia', t('Por dia')], ['semana', t('Por semana')], ['mes', t('Por mês')]];
                  const nome = un === 'semana' ? tn('semana(s)', recur.every ?? 2) : un === 'mes' ? tn('mês(es)', recur.every ?? 2) : t('dias');
                  return (
                    <View style={{ gap: 10, paddingLeft: 30 }}>
                      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                        {unidades.map(([u, l]) => <Chip key={u} cor={cor} small on={un === u} label={l}
                          onPress={() => onChange({ ...recur, unit: u, days: u === 'semana' ? recur.days?.slice(0, 1) : undefined, monthDay: u === 'mes' ? recur.monthDay : undefined })} />)}
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Text style={{ color: C.text }}>{t('A cada')}</Text>
                        <Chip cor={cor} small on={false} label="−" onPress={() => onChange({ ...recur, every: Math.max(1, (recur.every ?? 2) - 1) })} />
                        <Text style={{ color: C.text, fontWeight: '700', fontSize: 16 }}>{recur.every ?? 2}</Text>
                        <Chip cor={cor} small on={false} label="+" onPress={() => onChange({ ...recur, every: (recur.every ?? 2) + 1 })} />
                        <Text style={{ color: C.text }}>{nome}</Text>
                      </View>
                      {un === 'semana' && (<>
                        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Na (opcional):')}</Text>
                        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                          {[1, 2, 3, 4, 5, 6, 0].map(d => {
                            const on = recur.days?.[0] === d;
                            return (
                              <Pressable key={d} onPress={() => onChange({ ...recur, days: on ? undefined : [d] })}
                                style={{ width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? cor : C.card2 }}>
                                <Text style={{ color: on ? '#fff' : C.text, fontWeight: '700' }}>{t(DIAS_CURTOS[d])}</Text>
                              </Pressable>
                            );
                          })}
                        </View>
                      </>)}
                      {un === 'mes' && (<>
                        <Text style={{ color: C.sub, fontSize: 12 }}>{t('No dia (opcional):')}</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(n => {
                            const on = recur.monthDay === n;
                            return (
                              <Pressable key={n} onPress={() => onChange({ ...recur, monthDay: on ? undefined : n })}
                                style={{ width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? cor : C.card2 }}>
                                <Text style={{ color: on ? '#fff' : C.text, fontSize: 13 }}>{n}</Text>
                              </Pressable>
                            );
                          })}
                        </View>
                      </>)}
                    </View>
                  );
                })()}
              </View>
            ))}
  </>);
}
